import { useEffect } from 'react';
import { TvButton, useBackHandler, useFocusScope } from '../tv';

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  body: string;
  confirmLabel: string;
  cancelLabel?: string;
  destructive?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  testId?: string;
}

/**
 * A modal that a remote cannot escape by accident.
 *
 * `useFocusScope` confines the D-pad to the dialog and returns focus to whatever
 * opened it; `useBackHandler` makes Back mean "cancel" for as long as it is up,
 * shadowing whatever Back meant on the screen underneath. Cancel is focused
 * first on purpose — for a destructive choice, the safe option should be the one
 * already under OK.
 */
export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  cancelLabel = 'Cancel',
  destructive = false,
  onConfirm,
  onCancel,
  testId,
}: ConfirmDialogProps) {
  const scopeRef = useFocusScope<HTMLDivElement>(open);
  useBackHandler(onCancel, open);

  // A dialog that opens while the page behind it is still scrolling or
  // animating should not inherit a stale hover state on a tablet.
  useEffect(() => {
    if (!open) return;
    document.documentElement.dataset.modal = 'true';
    return () => {
      delete document.documentElement.dataset.modal;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="dialog-backdrop" role="presentation">
      <div
        ref={scopeRef}
        className="dialog"
        role="alertdialog"
        aria-modal="true"
        aria-label={title}
        data-testid={testId}
      >
        <h2 className="dialog__title">{title}</h2>
        <p className="dialog__body">{body}</p>
        <div className="dialog__buttons">
          <TvButton variant="ghost" data-testid={`${testId}-cancel`} onClick={onCancel}>
            {cancelLabel}
          </TvButton>
          <TvButton
            variant={destructive ? 'danger' : 'primary'}
            data-testid={`${testId}-confirm`}
            onClick={onConfirm}
          >
            {confirmLabel}
          </TvButton>
        </div>
      </div>
    </div>
  );
}
